import { Children, isValidElement, useEffect, useState } from "react"
import { ArrowUpIcon, CircleAlertIcon, EllipsisVerticalIcon, LoaderCircleIcon, MessageCircleDashedIcon, PaperclipIcon, PlusIcon, RotateCwIcon, Undo2Icon } from "lucide-react"
import { Bubble, BubbleContent } from "@/components/ui/bubble"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group"
import { Message, MessageContent } from "@/components/ui/message"
import { MessageScroller, MessageScrollerButton, MessageScrollerContent, MessageScrollerItem, MessageScrollerProvider, MessageScrollerViewport, useMessageScroller } from "@/components/ui/message-scroller"
import { ToMarkdown } from "@/components/ui/tomarkdown"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { ChatThinking } from "./parts/thinking"
import { ChatToolCall, ChatToolCalls } from "./parts/toolcall"

export interface ChatQuickReply {
  id: string
  label: string
}

// What an agent turn did, in order. Text parts are markdown
export type ChatPart =
  | { type: "text", text: string }
  | { type: "thinking", text: string }
  | ({ type: "tool", id: string } & ChatToolCall)
  | { type: "error", text: string }

export interface ChatMessage {
  id: string
  role: "user" | "agent"
  text?: string
  image?: string
  quickReplies?: ChatQuickReply[]
  parts?: ChatPart[]
  footer?: React.ReactNode
}

type PartGroup = Exclude<ChatPart, { type: "tool" }> | { type: "tools", calls: ChatToolCall[] }

// Consecutive tool calls share one row
function group(parts: ChatPart[]) {
  const groups: PartGroup[] = []
  for (const part of parts) {
    const last = groups[groups.length - 1]
    if (part.type !== "tool") groups.push(part)
    else if (last?.type === "tools") last.calls.push(part)
    else groups.push({ type: "tools", calls: [part] })
  }
  return groups
}

function Parts({ parts, active }: { parts: ChatPart[], active: boolean }) {
  const groups = group(parts)
  if (!groups.length && active) return <span className="flex items-center gap-1 text-xs text-muted-foreground"><LoaderCircleIcon className="size-3 animate-spin" />Working…</span>
  return <>
    {groups.map((part, index) => {
      if (part.type === "thinking") return <ChatThinking key={index} text={part.text} active={active && index === groups.length - 1} />
      if (part.type === "tools") return <ChatToolCalls key={index} calls={part.calls} />
      if (part.type === "error") return <p key={index} className="flex items-start gap-1 text-sm text-red-600"><CircleAlertIcon className="size-4 shrink-0 mt-0.5" />{part.text}</p>
      return <Bubble key={index} variant="outline" className="max-w-full"><BubbleContent><ToMarkdown variant="chat">{part.text}</ToMarkdown></BubbleContent></Bubble>
    })}
  </>
}

function Composer({ children }: { children?: React.ReactNode }) {
  return <>{children}</>
}

// The scroller's own MutationObserver should follow new messages automatically, but keeping the
// bottom pinned every time reliably needs this explicit nudge via its exported hook
function AutoScrollToEnd({ messages }: { messages: ChatMessage[] }) {
  const { scrollToEnd } = useMessageScroller()
  useEffect(() => { scrollToEnd() }, [messages, scrollToEnd])
  return null
}

interface Props {
  className?: string
  children?: React.ReactNode
  messages: ChatMessage[]
  title?: string
  description?: string
  userLabel?: string
  agentLabel?: string
  placeholder?: string
  actions?: React.ReactNode
  busy?: boolean
  onSend?: (text: string) => void
  onQuickReply?: (id: string) => void
  onReset?: () => void
  onRewind?: (id: string) => void
}

// A turn can carry more than one text (say + an on_no_match prefix), joined with a blank line: split back into paragraphs
function paragraphs(text: string) {
  return text.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean)
}

export function Chat({ className, children, messages, title = "Chat", description, userLabel = "You", agentLabel = "Agent", placeholder = "Type a message", actions, busy, onSend, onQuickReply, onReset, onRewind }: Props) {
  const [input, setInput] = useState("")
  const composerSlot = Children.toArray(children).find(child => isValidElement(child) && child.type === Composer)

  const rewindable = (message: ChatMessage, index: number) => !!onRewind && message.role === "agent" && index < messages.length - 1

  function handleSend() {
    if (!input.trim() || busy) return
    onSend?.(input.trim())
    setInput("")
  }

  return <Card className={cn("flex h-full min-h-0 w-full flex-col gap-0 -ml-2", className)}>
    <CardHeader className="border-b p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {onReset && <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Reset conversation" onClick={onReset} disabled={busy}>
                <RotateCwIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>Reset</p></TooltipContent>
          </Tooltip>}
        </div>
      </div>
    </CardHeader>

    <CardContent className="min-h-0 flex-1 overflow-hidden bg-muted/25 p-0">
      {messages.length === 0 ? (
        <Empty className="h-full">
          <EmptyHeader>
            <EmptyMedia variant="icon"><MessageCircleDashedIcon /></EmptyMedia>
            <EmptyTitle>{agentLabel}</EmptyTitle>
            <EmptyDescription>Waiting for the first message.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <MessageScrollerProvider autoScroll defaultScrollPosition="end">
          <AutoScrollToEnd messages={messages} />
          <MessageScroller>
            <MessageScrollerViewport>
              <MessageScrollerContent className="justify-end p-4">
                {messages.map((message, index) => (
                  <MessageScrollerItem key={message.id} messageId={message.id}>
                    <Message align={message.role === "user" ? "end" : "start"} aria-label={message.role === "user" ? userLabel : agentLabel}>
                      <MessageContent className={cn(message.role === "agent" && "w-fit max-w-[80%] gap-1")}>
                        {(message.text || message.image || !!message.quickReplies?.length) && <Bubble variant={message.role === "user" ? "muted" : "outline"} className={cn(message.role === "agent" && "max-w-full")}>
                          {rewindable(message, index) && <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon-xs" aria-label="Message actions" disabled={busy} className="absolute top-1 right-1 text-muted-foreground"><EllipsisVerticalIcon /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => onRewind(message.id)}><Undo2Icon />Rewind to here</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>}
                          <BubbleContent className={cn("space-y-2", rewindable(message, index) && "pr-8")}>
                            {message.image && <img src={message.image} alt="" className="max-h-72 w-full rounded-md object-cover" />}
                            {message.text && paragraphs(message.text).map((paragraph, index) => <p key={index} className="whitespace-pre-wrap">{paragraph}</p>)}
                            {message.quickReplies && message.quickReplies.length > 0 && <div className={cn("-mx-3 -mb-2 flex flex-col", rewindable(message, index) && "-mr-8")}>
                              {message.quickReplies.map(reply => (
                                <button key={reply.id} type="button" disabled={busy || index < messages.length - 1} onClick={() => onQuickReply?.(reply.id)}
                                  className="border-t px-3 py-2 text-center! font-medium text-primary hover:bg-muted disabled:opacity-50">{reply.label}</button>
                              ))}
                            </div>}
                          </BubbleContent>
                        </Bubble>}
                        {message.parts && <Parts parts={message.parts} active={!!busy && index === messages.length - 1} />}
                        {message.footer}
                      </MessageContent>
                    </Message>
                  </MessageScrollerItem>
                ))}
              </MessageScrollerContent>
            </MessageScrollerViewport>
            <MessageScrollerButton />
          </MessageScroller>
        </MessageScrollerProvider>
      )}
    </CardContent>

    <CardFooter className="flex-col gap-2 pt-0 -mx-4 -mb-4">
      {composerSlot ?? <form className="w-full" onSubmit={e => { e.preventDefault(); handleSend() }}>
        <InputGroup>
          <InputGroupInput value={input} onChange={e => setInput(e.target.value)} placeholder={placeholder} disabled={busy} />
          <InputGroupAddon align="block-end" className="pt-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <InputGroupButton aria-label="Add files" type="button" size="icon-sm" variant="outline">
                  <PlusIcon />
                </InputGroupButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-44">
                <DropdownMenuItem disabled>
                  <PaperclipIcon />
                  Add Photos & Files
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <InputGroupButton type="submit" variant="default" size="icon-sm" disabled={!input.trim() || busy} className="ml-auto">
              <ArrowUpIcon />
              <span className="sr-only">Send</span>
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
      </form>}
    </CardFooter>
  </Card>
}

Chat.Composer = Composer
